/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// YearMultiSelect.jsx — a themed multi-select for forecast years. Replaces the
// single native <select> so the user can compare several years side by side.
// Styled to sit in the same `.selector-bar` as the other filter dropdowns.

import React, { useRef, useState } from "react";
import "./YearMultiSelect.css";

export default function YearMultiSelect({
  options = [],          // [{ value, label }]
  value = [],            // string[] of selected years
  onChange,              // (string[]) => void  — sorted ascending
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click (module-local; no shared hook dependency).
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    // Escape closes and hands focus back to the trigger. Without this the only
    // way out was a mouse click somewhere else on the page — the popup is not a
    // focus trap, so a keyboard user could tab past it and leave it hanging over
    // the grid. Found 2026-08-09 when a Playwright Escape failed to dismiss it.
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = new Set(value.map(String));

  const toggle = (yr) => {
    const next = new Set(selected);
    if (next.has(yr)) next.delete(yr); else next.add(yr);
    onChange([...next].sort());
  };

  const summary = value.length === 0
    ? "— Select —"
    : [...value].sort().join(", ");

  return (
    <div className="ym-root" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="selector-select ym-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        title={value.length ? `Years: ${summary}` : "Select one or more years"}
      >
        <span className="ym-trigger-label">{summary}</span>
        {value.length > 1 && <span className="ym-count">{value.length}</span>}
        <span className="ym-caret" aria-hidden>▾</span>
      </button>

      {/* A disclosure revealing a GROUP OF CHECKBOXES, not a listbox. This was
          `role="listbox" aria-multiselectable` over `<label><input
          type=checkbox>` children — a listbox whose children are not
          `role="option"` is invalid, and it made a screen reader announce a
          select-one control over what are really independent checkboxes.
          Native checkboxes already carry the right semantics; the group only
          needs a name. */}
      {open && (
        <div className="ym-pop" role="group" aria-label="Forecast years">
          {options.length === 0 && <div className="ym-empty">No years available</div>}
          {options.map((o) => {
            const checked = selected.has(String(o.value));
            return (
              <label key={o.value} className={`ym-item${checked ? " ym-item-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(String(o.value))}
                />
                <span className="ym-box" aria-hidden>
                  {checked && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <span className="ym-item-label">{o.label}</span>
              </label>
            );
          })}
          {value.length > 0 && (
            <button type="button" className="ym-clear" onClick={() => onChange([])}>
              Clear years
            </button>
          )}
        </div>
      )}
    </div>
  );
}
